import { buildBaseTag, escapeHtml, escapeStyleText, jsonForInlineScript } from '../../utils/sandboxedPreview';

/**
 * Документ для превью компонента в iframe sandbox="allow-scripts" (без allow-same-origin).
 * Всё, что раньше тестер делал с DOM приложения (CSS тем и компонента в <head>, data-theme на <html>,
 * активация карусели, карты Leaflet, плашки источников изображений), выполняется внутри iframe.
 */

export const COMPONENT_PREVIEW_HEIGHT_MESSAGE = 'component-tester:preview-height';

export interface ComponentPreviewDocumentOptions {
  html: string;
  css: string;
  themesCss: string;
  theme: string;
  language: string;
  showImageSources: boolean;
  /** Пары [нормализованный URL изображения, источник]. */
  imageSources: Array<[string, string]>;
  baseHref?: string;
}

const FRAME_CSS = `
html, body { margin: 0; padding: 0; }
#component-tester-root { display: flow-root; position: relative; }
.image-source-overlay { position: absolute; top: 4px; left: 4px; padding: 2px 6px; font: 700 9px/1.4 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; text-transform: uppercase; letter-spacing: 0.5px; border-radius: 3px; z-index: 100; pointer-events: none; box-shadow: 0 1px 3px rgba(0,0,0,0.3); color: #fff; }
.source-unsplash { background: #000; }
.source-pexels { background: #05A081; }
.source-pixabay { background: #6C5CE7; }
.source-xsgames { background: #FF6B35; }
.source-pravatar { background: #9C27B0; }
.source-randomuser { background: #2196F3; }
.source-processed, .source-local { background: #4CAF50; }
.source-optimized { background: #8BC34A; }
.source-placeholder, .source-picsumplaceholder { background: #FF9800; }
.source-unknown { background: #9E9E9E; }
.source-error { background: #F44336; }
`;

// Скрипт исполняется только внутри sandboxed iframe (opaque origin).
const FRAME_RUNTIME = `
(function () {
  var cfg = window.__COMPONENT_TESTER__ || {};
  var root = document.getElementById('component-tester-root');

  // --- Высота: iframe растёт по содержимому. Если содержимое растёт вместе с высотой
  // iframe (min-height: 100vh и т.п.), авторазмер останавливается, чтобы не расти бесконечно.
  var lastSentHeight = -1e9;
  var sentAtViewport = window.innerHeight;
  var viewportLocked = false;
  function measure() { return root ? Math.ceil(root.getBoundingClientRect().height) : 0; }
  function reportHeight() {
    if (viewportLocked || !root) return;
    var height = measure();
    if (Math.abs(height - lastSentHeight) < 2) return;
    lastSentHeight = height;
    sentAtViewport = window.innerHeight;
    try { window.parent.postMessage({ type: cfg.messageType, height: height }, '*'); } catch (e) {}
  }
  window.addEventListener('resize', function () {
    var viewport = window.innerHeight;
    if (viewport === sentAtViewport) { reportHeight(); return; }
    if (viewport > sentAtViewport && measure() - lastSentHeight >= viewport - sentAtViewport - 1) {
      viewportLocked = true;
    }
    sentAtViewport = viewport;
    reportHeight();
  });
  if (window.ResizeObserver && root) {
    new ResizeObserver(function () { if (window.innerHeight === sentAtViewport) reportHeight(); }).observe(root);
  }
  window.addEventListener('load', reportHeight);
  document.addEventListener('load', reportHeight, true);
  document.addEventListener('error', reportHeight, true);

  // --- Карусель: первый слайд и первая точка активны.
  function initCarousel() {
    var slides = document.querySelectorAll('.tpl-carousel__slide');
    if (!slides.length) return;
    for (var i = 0; i < slides.length; i++) slides[i].classList.remove('is-active');
    slides[0].classList.add('is-active');
    var dots = document.querySelectorAll('.tpl-carousel-dot');
    if (!dots.length) return;
    for (var j = 0; j < dots.length; j++) dots[j].classList.remove('is-active');
    dots[0].classList.add('is-active');
  }

  // --- Источники изображений (плашки поверх img).
  var SOURCE_LABELS = { unsplash: 'Unsplash', pexels: 'Pexels', pixabay: 'Pixabay', xsgames: 'XSGames', pravatar: 'Pravatar', randomuser: 'RandomUser', placeholder: 'Placeholder', local: 'Local', unknown: 'Unknown' };
  function showImageSources() {
    if (!cfg.showImageSources || !root) return;
    var entries = cfg.imageSources || [];
    var images = root.querySelectorAll('img');
    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      var parent = img.parentElement;
      if (!parent) continue;
      var existing = parent.querySelector('.image-source-overlay');
      if (existing) existing.remove();
      var imgUrl = (img.src || '').replace(/^https?:/, '').split('?')[0];
      var imgFilename = imgUrl.split('/').pop() || '';
      var source = 'unknown';
      for (var k = 0; k < entries.length; k++) {
        var urlFilename = String(entries[k][0]).split('/').pop() || '';
        if (urlFilename && imgFilename && (imgFilename.indexOf(urlFilename.split('.')[0]) !== -1 || urlFilename.indexOf(imgFilename.split('.')[0]) !== -1)) {
          source = String(entries[k][1]);
          break;
        }
      }
      if (source === 'unknown') {
        if (imgUrl.indexOf('unsplash') !== -1) source = 'unsplash';
        else if (imgUrl.indexOf('pexels') !== -1) source = 'pexels';
        else if (imgUrl.indexOf('pixabay') !== -1) source = 'pixabay';
        else if (imgUrl.indexOf('xsgames') !== -1) source = 'xsgames';
        else if (imgUrl.indexOf('pravatar') !== -1) source = 'pravatar';
        else if (imgUrl.indexOf('randomuser') !== -1) source = 'randomuser';
        else if (imgUrl.indexOf('picsum') !== -1) source = 'placeholder';
        else if (imgUrl.indexOf('/images/') !== -1) source = entries.length > 0 ? String(entries[0][1]) : 'local';
      }
      var overlay = document.createElement('div');
      overlay.className = 'image-source-overlay source-' + source.replace(/[^a-z0-9_-]/gi, '');
      overlay.textContent = SOURCE_LABELS[source] || source.toUpperCase();
      parent.style.position = 'relative';
      parent.appendChild(overlay);
    }
  }

  // --- Карты Leaflet для contact-компонентов.
  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  var MAP_IDS = ['contact-extended-map', 'contact-map-only-map', 'contact-map-background'];
  function hasMaps() {
    for (var i = 0; i < MAP_IDS.length; i++) if (document.getElementById(MAP_IDS[i])) return true;
    return false;
  }
  function loadLeafletScript(done) {
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }
    var script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.onload = function () { setTimeout(function () { if (window.L) done(); else console.error('Leaflet загружен, но L не определён'); }, 100); };
    script.onerror = function () { console.error('Ошибка загрузки Leaflet'); };
    document.head.appendChild(script);
  }
  function ensureLeaflet(done) {
    if (window.L) { done(); return; }
    var existing = document.querySelector('script[src*="leaflet"]');
    if (!existing) { loadLeafletScript(done); return; }
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      if (window.L) { clearInterval(timer); done(); return; }
      if (attempts >= 100) { clearInterval(timer); existing.remove(); loadLeafletScript(done); }
    }, 100);
  }
  function createMap(container, lat, lng, zoom, invalidate) {
    try {
      var L = window.L;
      var map = L.map(container).setView([lat, lng], zoom);
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        referrerPolicy: 'strict-origin-when-cross-origin',
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19
      }).addTo(map);
      L.marker([lat, lng]).addTo(map);
      if (invalidate) setTimeout(function () { map.invalidateSize(); reportHeight(); }, 100);
    } catch (error) {
      console.error('Ошибка инициализации карты', error);
    }
  }
  function initMap(id, isBackground) {
    var container = document.getElementById(id);
    if (!container) return;
    var lat = parseFloat(container.dataset.mapLat || '0');
    var lng = parseFloat(container.dataset.mapLng || '0');
    var zoom = parseInt(container.dataset.mapZoom || '15', 10);
    if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return;
    if (!isBackground) { createMap(container, lat, lng, zoom, false); return; }
    var rect = container.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) { createMap(container, lat, lng, zoom, true); return; }
    setTimeout(function () {
      var retry = container.getBoundingClientRect();
      if (retry.width > 0 && retry.height > 0) createMap(container, lat, lng, zoom, true);
    }, 200);
  }
  function initMaps() {
    if (!window.L) return;
    initMap('contact-extended-map', false);
    initMap('contact-map-only-map', false);
    initMap('contact-map-background', true);
    reportHeight();
  }

  function start() {
    initCarousel();
    reportHeight();
    setTimeout(function () { showImageSources(); reportHeight(); }, 100);
    if (hasMaps()) {
      setTimeout(function () { ensureLeaflet(function () { setTimeout(initMaps, 100); }); }, 200);
    }
    setTimeout(reportHeight, 1000);
    setTimeout(reportHeight, 2500);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
`;

export const buildComponentPreviewDocument = ({
  html,
  css,
  themesCss,
  theme,
  language,
  showImageSources,
  imageSources,
  baseHref
}: ComponentPreviewDocumentOptions): string => {
  const config = {
    messageType: COMPONENT_PREVIEW_HEIGHT_MESSAGE,
    showImageSources,
    imageSources
  };

  return `<!DOCTYPE html>
<html lang="${escapeHtml(language || 'ru')}" data-theme="${escapeHtml(theme)}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${buildBaseTag(baseHref)}
<style>${escapeStyleText(themesCss)}</style>
<style>${escapeStyleText(css)}</style>
<style>${FRAME_CSS}</style>
</head>
<body>
<div id="component-tester-root">${html}</div>
<script>window.__COMPONENT_TESTER__ = ${jsonForInlineScript(config)};</script>
<script>${FRAME_RUNTIME}</script>
</body>
</html>`;
};
